// @ts-check
if (!customElements.get('carousel-component')) {
  class CarouselComponent extends HTMLElement {
    constructor() {
      super()
      this.init()
    }

    init() {
      this.selectors = {
        carousel: '.embla',
        currentSlideDisplay: '.slider-counter--current',
        emblaContainer: '.embla__container',
        next: '[name="next"]',
        previous: '[name="previous"]',
        pausePlay: '.slideshow__autoplay'
      }

      this.autoplayDefaults = {
        delay: 3000,
        /**
         * these settings cause the carousel to restart on every interaction. we have to update the button to match
         * @see onSlideChange
         */
        stopOnMouseEnter: true,
        stopOnInteraction: false,
        autoplayRoot: (embla) => embla.parentElement
      }

      // other settings controlled in pseudo options
      this.defaults = {
        loop: true,
        speed: 10,
      }

      this.loadCarousel = this.loadCarousel.bind(this)

      this.sliderSetup()
      this.updateSlideControlButtons()
      this.loadCarousel()

      if (Shopify.designMode) {
        this.setupThemeEditor()
      }
    }

    /**
     * destroy / reinitialize carousel when changes in theme editor
     * show slide block when a slide is chosen in sidebar
     */
    setupThemeEditor() {
      document.addEventListener('shopify:section:unload', ({ detail: { sectionId } }) => {
        if (sectionId == this.dataset.sectionId) {
          try {
            this.carousel.destroy()
          } catch (e) {
            console.log(e)
          }
        }
      })

      document.addEventListener('shopify:section:load', ({ detail: { sectionId } }) => {
        if (sectionId == this.dataset.sectionId) {
          this.setupCarousel()
        }
      })

      document.addEventListener('shopify:block:select', (event) => {
        const blockSelectedIsSlide = event.target.classList.contains('slideshow__slide');
        if (!blockSelectedIsSlide) return;

        this.pause();

        setTimeout(() => {
          this.carousel.scrollTo(Array.from(this.querySelector(this.selectors.emblaContainer).children).indexOf(event.target), true)
        }, 200);
      });

      document.addEventListener('shopify:block:deselect', (event) => {
        const blockDeselectedIsSlide = event.target.classList.contains('slideshow__slide');
        if (!blockDeselectedIsSlide) return;
        if (this.autoplayButtonIsSetToPlay) this.play();
      });
    }

    /**
     * A lot of legacy setup to accomodate methods that were copied over.
     */
    sliderSetup() {
      this.currentPage = 1
      this.slider = this.querySelector('[id^="Slider-"]');
      this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
      this.sliderControlWrapper = this.querySelector('.slider-buttons');

      if (this.sliderControlWrapper) {
        this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
        this.sliderControlLinksArray.forEach(link => link.addEventListener('click', this.linkToSlide.bind(this)));
      }

      this.sliderItemsToShow = Array.from(this.sliderItems).filter(element => element.clientWidth > 0);

      this.autoplayButtonIsSetToPlay = true
    }

    /**
     * Settings object to be consumed by embla
     *
     * @returns {Object}
     */
    getSettings() {
      const container = /** @type {HTMLElement} */ (this.querySelector(this.selectors.emblaContainer))

      return {
        autoplay: container.dataset.autoplay === 'true',
        autoplayOptions: {
          delay: parseInt(container.dataset.speed, 10) * 1000 || this.autoplayDefaults.delay,
          stopOnMouseEnter: this.autoplayDefaults.stopOnMouseEnter,
          stopOnInteraction: this.autoplayDefaults.stopOnInteraction,
          autoplayRoot: this.autoplayDefaults.autoplayRoot
        }
      }
    }

    /**
     * wait until embla is available and then setup the carousel
     */
    loadCarousel() {
      document.removeEventListener('emblaLoad', this.loadCarousel)
      document.removeEventListener('emblaAutoplayLoad', this.loadCarousel)

      if (
        typeof window.EmblaCarousel !== 'undefined' &&
        typeof window.EmblaCarouselAutoplay !== 'undefined'
      ) {
        this.setupCarousel()
        return
      }

      document.addEventListener('emblaLoad', this.loadCarousel)
      document.addEventListener('emblaAutoplayLoad', this.loadCarousel)
    }

    setupCarousel() {
      const { autoplay, autoplayOptions } = this.getSettings()

      const plugins = []
      if (autoplay) {
        this.autoplay = EmblaCarouselAutoplay(autoplayOptions)
        plugins.push(this.autoplay)
      }

      // sometimes not all the slides are in the DOM without waiting
      setTimeout(() => {
        this.initializeCarousel({ plugins })
        this.bindEvents()
      }, 0)
    }

    initializeCarousel({ plugins }) {
      this.carousel = EmblaCarousel(this.querySelector(this.selectors.carousel), {}, plugins)
    }

    bindEvents() {
      const next = this.querySelector(this.selectors.next)
      const prev = this.querySelector(this.selectors.previous)
      this.sliderAutoplayButton = this.querySelector(this.selectors.pausePlay);

      if (this.sliderAutoplayButton) {
        this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      }
      next.addEventListener('click', () => this.nextSlide())
      prev.addEventListener('click', () => this.previousSlide())

      this.carousel.on('select', this.onSlideChange.bind(this))
      this.carousel.on('reInit', () => {
        this.autoplayButtonIsSetToPlay = false
        this.autoPlayToggle()
      })
    }

    nextSlide() {
      if (this.autoplay) this.autoplay.reset()
      this.carousel.scrollNext()
    }

    previousSlide() {
      if (this.autoplay) this.autoplay.reset()
      this.carousel.scrollPrev()
    }

    onSlideChange() {
      const previousPage = this.currentPage
      this.currentPage = this.carousel.selectedScrollSnap() + 1
      const currentPageDisplay = this.querySelector(this.selectors.currentSlideDisplay)

      if (currentPageDisplay) currentPageDisplay.textContent = this.currentPage

      this.updateSlideControlButtons()
      this.setSlideVisibility()

      // reset play button state when carousel restarts
      if (this.autoplay) {
        this.autoplayButtonIsSetToPlay = true
        this.togglePlayButtonState(false)
      }

      // listened for by the media gallery component
      if (this.currentPage != previousPage) {
        this.dispatchEvent(new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1]
          }
        }));
      }
    }

    // Legacy methods / copied from Slider/Slideshow

    updateSlideControlButtons() {
      this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');

      if (!this.sliderControlButtons.length) return;

      this.sliderControlButtons.forEach(link => {
        link.classList.remove('slider-counter__link--active');
        link.removeAttribute('aria-current');
      });
      this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
      this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
    }

    play() {
      this.slider.setAttribute('aria-live', 'off');
      if (this.autoplay) {
        this.autoplay.play()
      }
    }

    pause() {
      this.slider.setAttribute('aria-live', 'polite');
      if (this.autoplay) {
        this.autoplay.stop()
      }
    }

    togglePlayButtonState(pauseAutoplay) {
      if (this.sliderAutoplayButton) {
        if (pauseAutoplay) {
          this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
          this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
        } else {
          this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
          this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
        }
      }
    }

    autoPlayToggle() {
      this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
      this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
      this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
    }

    setSlideVisibility() {
      this.sliderItemsToShow.forEach((item, index) => {
        const button = item.querySelector('a');
        if (index === this.currentPage - 1) {
          if (button) button.removeAttribute('tabindex');
          item.setAttribute('aria-hidden', 'false');
          item.removeAttribute('tabindex');
        } else {
          if (button) button.setAttribute('tabindex', '-1');
          item.setAttribute('aria-hidden', 'true');
          item.setAttribute('tabindex', '-1');
        }
      });
    }

    linkToSlide(event) {
      event.preventDefault();
      const newSlide = this.sliderControlLinksArray.indexOf(event.currentTarget)
      this.carousel.scrollTo(newSlide);
    }
  }

  customElements.define('carousel-component', CarouselComponent);
}
